import { logger } from '../../../utils/logger';
/**
import { logger } from '../../../utils/logger';
 * Pip's Guild Quest Hub
 * Main entry point for Pip's Guild Quest minigame
 */

import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonSpinner,
  IonIcon,
  IonBackButton,
  IonButtons,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { documentTextOutline, timeOutline, peopleOutline } from 'ionicons/icons';
import { getQuestTemplates, getGroupQuests } from '../../../services/pipsGuildQuestService';
import { QuestTemplate, GuildQuest } from '../../../types/pipsGuildQuest';
import { supabase } from '../../../services/supabase';
import './PipsGuildQuestHub.css';

interface ActiveQuestWithTemplate extends GuildQuest {
  templateName?: string;
  templateTheme?: string;
  templateDifficulty?: string;
}

const PipsGuildQuestHub: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<QuestTemplate[]>([]);
  const [activeQuests, setActiveQuests] = useState<ActiveQuestWithTemplate[]>([]);

  useEffect(() => {
    loadQuestData();
  }, []);

  const loadQuestData = async () => {
    setLoading(true);
    try {
      const templatesData = await getQuestTemplates();
      setTemplates(templatesData);

      // Load user's active quests
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Query for quests where the user is in the turn_order array
        const { data: questsData, error } = await supabase
          .from('guild_quests')
          .select('*')
          .contains('turn_order', [user.id])
          .eq('status', 'active')
          .order('created_at', { ascending: false });

        if (!error && questsData) {
          logger.log('📋 Loaded active quests:', questsData);

          // Enrich quests with template information
          const enrichedQuests = await Promise.all(
            questsData.map(async (quest) => {
              const questTemplateId = (quest as any).quest_template_id || quest.questTemplateId;
              const { data: template } = await supabase
                .from('quest_templates')
                .select('name, theme, difficulty')
                .eq('id', questTemplateId)
                .single();

              return {
                ...quest,
                templateName: template?.name,
                templateTheme: template?.theme,
                templateDifficulty: template?.difficulty,
              } as ActiveQuestWithTemplate;
            })
          );

          setActiveQuests(enrichedQuests);
        }
      }
    } catch (error) {
      logger.error('Error loading quest data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'normal':
        return 'warning';
      case 'hard':
        return 'danger';
      default:
        return 'medium';
    }
  };

  const getThemeEmoji = (theme: string) => {
    switch (theme) {
      case 'gothic horror':
        return '🏰';
      case 'fae fantasy':
        return '🌿';
      case 'high fantasy':
        return '⚔️';
      case 'cosmic horror':
        return '🌌';
      default:
        return '📜';
    }
  };

  const handleStartQuest = (templateId: string) => {
    history.push(`/games/guild-quest/create/${templateId}`);
  };

  const handleContinueQuest = (questId: string) => {
    history.push(`/games/guild-quest/play/${questId}`);
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/games" />
            </IonButtons>
            <IonTitle>Pip's Guild Quest</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="loading-container">
            <IonSpinner />
            <p>Loading quests...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/games" />
          </IonButtons>
          <IonTitle>Pip's Guild Quest</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding guild-quest-hub">
        {/* Game Description */}
        <div className="game-description">
          <h2>⚔️ Pip's Guild Quest</h2>
          <p>
            Embark on cooperative story-driven adventures with your party. Make choices
            together, face dangers, and discover multiple endings based on your decisions.
          </p>
        </div>

        {/* Active Quests */}
        {activeQuests.length > 0 && (
          <div className="section">
            <h3>
              <IonIcon icon={documentTextOutline} /> Active Quests
            </h3>
            <IonList>
              {activeQuests.map((quest) => {
                const turnOrder = quest.turnOrder as string[];
                const currentTurnUserId = (quest as any).current_turn_user_id || quest.currentTurnUserId;
                return (
                  <IonCard key={quest.id} className="quest-card active-quest">
                    <IonCardHeader>
                      <IonCardTitle>
                        {quest.templateName || 'Quest in Progress'}
                      </IonCardTitle>
                      <IonCardSubtitle>
                        <IonBadge color="success">In Progress</IonBadge>
                        {quest.templateDifficulty && (
                          <IonBadge color={getDifficultyColor(quest.templateDifficulty)} className="ion-margin-start">
                            {quest.templateDifficulty}
                          </IonBadge>
                        )}
                      </IonCardSubtitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <div className="quest-info">
                        <IonIcon icon={peopleOutline} />
                        <span>{turnOrder?.length || 0} players</span>
                      </div>
                      {currentTurnUserId && (
                        <div className="quest-info">
                          <IonIcon icon={timeOutline} />
                          <span>Turn in progress</span>
                        </div>
                      )}
                      <IonButton
                        expand="block"
                        onClick={() => handleContinueQuest(quest.id)}
                        className="ion-margin-top"
                      >
                        Continue Quest
                      </IonButton>
                    </IonCardContent>
                  </IonCard>
                );
              })}
            </IonList>
          </div>
        )}

        {/* Available Quests */}
        <div className="section">
          <h3>
            <IonIcon icon={documentTextOutline} /> Available Quests
          </h3>

          {templates.length === 0 ? (
            <IonCard>
              <IonCardContent>
                <p className="ion-text-center">No quests available yet.</p>
                <p className="ion-text-center ion-text-color-medium">
                  Check back soon for new adventures!
                </p>
              </IonCardContent>
            </IonCard>
          ) : (
            <div className="quest-grid">
              {templates.map((template) => (
                <IonCard key={template.id} className="quest-card">
                  <IonCardHeader>
                    <div className="quest-icon">{getThemeEmoji(template.theme)}</div>
                    <IonCardTitle>{template.name}</IonCardTitle>
                    <IonCardSubtitle>
                      <IonBadge color={getDifficultyColor(template.difficulty)}>
                        {template.difficulty}
                      </IonBadge>
                      <span className="theme-badge">{template.theme}</span>
                    </IonCardSubtitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <p className="quest-description">{template.description}</p>

                    <div className="quest-details">
                      <div className="detail-item">
                        <IonIcon icon={peopleOutline} />
                        <span>
                          {template.minPlayers}-{template.maxPlayers} players
                        </span>
                      </div>
                      <div className="detail-item">
                        <IonIcon icon={documentTextOutline} />
                        <span>~{template.estimatedNodes} nodes</span>
                      </div>
                    </div>

                    <IonButton
                      expand="block"
                      onClick={() => handleStartQuest(template.id)}
                      className="ion-margin-top"
                    >
                      Start Quest
                    </IonButton>
                  </IonCardContent>
                </IonCard>
              ))}
            </div>
          )}
        </div>

        {/* How to Play */}
        <div className="section how-to-play">
          <h3>How to Play</h3>
          <IonList>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h4>1. Start a Quest</h4>
                <p>Choose a quest and invite your party members from your group.</p>
              </IonLabel>
            </IonItem>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h4>2. Select Your Class</h4>
                <p>Each player chooses a class: Warrior, Mage, Rogue, Cleric, or Ranger.</p>
              </IonLabel>
            </IonItem>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h4>3. Make Decisions</h4>
                <p>
                  Take turns making choices that shape the story. You have 12 hours per turn.
                </p>
              </IonLabel>
            </IonItem>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h4>4. Battle Together</h4>
                <p>Face enemies in turn-based combat using your class abilities.</p>
              </IonLabel>
            </IonItem>
            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                <h4>5. Discover Endings</h4>
                <p>
                  Your choices lead to different endings. Replay to unlock new classes and
                  rewards!
                </p>
              </IonLabel>
            </IonItem>
          </IonList>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PipsGuildQuestHub;
